// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import DragHandleIcon from "@material-ui/icons/DragHandle";
import { mdiDownload, mdiPencil, mdiTestTube } from "@mdi/js";
import { Button, Divider } from "antd";
import Checkbox from "antd/lib/checkbox/Checkbox";
import { useAtomValue, useSetAtom } from "jotai";
import styled from "styled-components";

import { IGroupBy, IGroupByListColumn } from "models";

import { Tooltip } from "components/base";
import { ChevronDown, ChevronRight } from "components/icons";
import FieldGroupEditor from "components/user-defined-columns/FieldGroupEditor";
import { fieldGroupEditorStateAtom } from "components/user-defined-columns/atoms/fieldGroupAtoms";

import FieldListItem from "./FieldListItem";
import { useGroupByStore } from "./GroupByStoreProvider";
import {
  selectCategoryColors,
  selectCheckedFields,
  selectCustomFieldsEditToggle,
  selectEntityKind,
  selectIsFiltering,
  selectIsForecastToggleOn,
  selectProps,
  selectSelectedField,
  selectSelectedForecastFolder,
  selectSelectedForecastFolderName,
  selectSelectedPdenSource,
  selectSelectedTab
} from "./groupBySelectors";
import { useFieldGroupDownload, useSelectedCategory } from "./hooks";
import useCustomColumnOrderMutation from "./hooks/useMutationCustomColumnOrder";
import useGeomBinOrderMutation from "./hooks/useMutationGeomBinOrder";

export enum GroupType {
  FieldGroup = "field",
  BinGroup = "bin"
}

interface FieldSelectionData {
  field: IGroupByListColumn;
  onChange?: (
    value: IGroupBy | IGroupByListColumn | Record<string, unknown>,
    isForecastToggleOn?: boolean,
    selectedForecastFolder?: string,
    selectedForecastFolderName?: string,
    selectedPdenSource?: number
  ) => void;
  isForecastToggleOn?: boolean;
  selectedForecastFolder?: string;
  selectedForecastFolderName?: string;
  selectedPdenSource?: number;
}

type FieldGroup = {
  groupName: string;
  fields: IGroupByListColumn[];
  groups: FieldGroup[];
  dragHandleProps;
  isDragging: boolean;
  shortcutsEnabled: boolean;
  groupType?: GroupType;
  active?: boolean;
  focusedIndex?: number;
  fieldStartIndex?: number;
  projectId?: string;
  isPerformanceDateRange?: boolean;
  isDisablingManagement?: boolean;
  onItemRef?: (index: number, el: Maybe<HTMLElement>) => void;
  onFieldClick?: (fieldData: FieldSelectionData) => void;
  onFieldDoubleClick?: (fieldData: FieldSelectionData) => void;
};

const FieldGroup = ({
  groupName,
  fields,
  groups,
  dragHandleProps,
  isDragging,
  shortcutsEnabled,
  groupType = GroupType.FieldGroup,
  active = false,
  focusedIndex = -1,
  fieldStartIndex = 0,
  projectId,
  isPerformanceDateRange = false,
  isDisablingManagement = false,
  onItemRef,
  onFieldClick,
  onFieldDoubleClick
}: FieldGroup) => {
  const isTestGroup = groupName.endsWith("||test");
  const titleRow = (
    <FieldGroupTitle>
      <GroupName>{groupName.replace("||test", "")}</GroupName>
      {isTestGroup && (
        <TestIconWrapper>
          <Tooltip title="Non-published test fields">
            <Icon path={mdiTestTube} size={1.2} />
          </Tooltip>
        </TestIconWrapper>
      )}
    </FieldGroupTitle>
  );

  const [fieldsList, setFieldsList] = useState<IGroupByListColumn[]>(fields);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [editorVisible, setEditorVisible] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const groupHeaderRef = useRef<HTMLSpanElement>(null);

  const fieldGroupEditorState = useAtomValue(fieldGroupEditorStateAtom);
  const setFieldGroupEditorState = useSetAtom(fieldGroupEditorStateAtom);

  // Group header always gets fieldStartIndex as its index
  const groupHeaderIndex = fieldStartIndex;
  const isGroupHeaderActive = active && focusedIndex === groupHeaderIndex;

  const { isUserDefinedColumn, isOrgDefinedColumn, isProjectDefinedColumn } =
    useSelectedCategory();
  const customFieldsEditToggle = useGroupByStore(selectCustomFieldsEditToggle);
  const selectedTab = useGroupByStore(selectSelectedTab);
  const checkedFields = useGroupByStore(selectCheckedFields);
  const entityKind = useGroupByStore(selectEntityKind);
  const setCheckedFields = useGroupByStore((s) => s.setCheckedFields);
  const storeProps = useGroupByStore(selectProps);
  const selectedField = useGroupByStore(selectSelectedField);
  const isForecastToggleOn = useGroupByStore(selectIsForecastToggleOn);
  const selectedForecastFolder = useGroupByStore(selectSelectedForecastFolder);
  const selectedForecastFolderName = useGroupByStore(selectSelectedForecastFolderName);
  const selectedPdenSource = useGroupByStore(selectSelectedPdenSource);
  const isFiltering = useGroupByStore(selectIsFiltering);
  const categoryColors = useGroupByStore(selectCategoryColors);
  const setHoveredField = useGroupByStore((s) => s.setHoveredField);
  const prevFieldsRef = useRef(fields);
  const isCustomColumn = useMemo(() => {
    return isOrgDefinedColumn || isUserDefinedColumn || isProjectDefinedColumn;
  }, [isOrgDefinedColumn, isUserDefinedColumn, isProjectDefinedColumn]);
  const isProjectDefined = isProjectDefinedColumn;
  const enabled =
    (isUserDefinedColumn || isOrgDefinedColumn || isProjectDefinedColumn) &&
    customFieldsEditToggle &&
    fields.length > 1;

  // Download hook
  const { isDownloading, handleDownload } = useFieldGroupDownload({
    fieldGroup: groupName,
    entityKind,
    isOrgDefinedColumn,
    isProjectDefinedColumn
  });

  useEffect(() => {
    if (isDragging) {
      setIsCollapsed(true);
    }
  }, [isDragging]);

  useEffect(() => {
    if (fieldGroupEditorState.visible && fieldGroupEditorState.fieldGroup) {
      if (groupName === fieldGroupEditorState.fieldGroup) {
        setEditorVisible(true);
      }
    }
  }, [fieldGroupEditorState, groupName]);

  // Register group header ref with parent using the same onItemRef callback
  useEffect(() => {
    if (onItemRef) {
      onItemRef(groupHeaderIndex, groupHeaderRef.current);
    }
  }, [onItemRef, groupHeaderIndex, groupHeaderRef.current]);

  useEffect(() => {
    if (
      JSON.stringify(fields) !== JSON.stringify(prevFieldsRef.current) ||
      fieldsList.length === 0
    ) {
      setFieldsList(fields);
    }
    prevFieldsRef.current = fields;
  }, [fields]);
  const { mutate: fieldMutate } = useCustomColumnOrderMutation(
    isOrgDefinedColumn,
    entityKind,
    groupName,
    projectId
  );
  const { mutate: geomBinMutate } = useGeomBinOrderMutation(projectId);

  if (!fieldsList.length) {
    return null;
  }

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleEditDataClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditorVisible(true);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) {
      return;
    }

    const reorderedItems = JSON.parse(JSON.stringify(fieldsList));
    const [movedItem] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, movedItem);
    reorderedItems.forEach((item, index) => {
      item.withinSubgroupOrder = index;
    });

    const updatedItems = reorderedItems.map((item, index) => ({
      id: item.id,
      withinSubgroupOrder: index
    }));

    setFieldsList(reorderedItems);
    if (selectedTab === "fields") {
      fieldMutate({ groupName, items: updatedItems, projectId, isProjectDefined });
    } else {
      geomBinMutate({ groupName, items: updatedItems, projectId, isProjectDefined });
    }
  };

  const onCheckboxChange = () => {
    const fieldsInGroup = fields.map((f) => f.id);
    const hasCheckedItem =
      fields.filter((obj) => checkedFields.includes(obj.id)).map((obj) => obj.id).length >
      0;
    setCheckedFields(
      hasCheckedItem
        ? checkedFields.filter((item) => !fieldsInGroup.includes(item))
        : [...new Set(checkedFields.concat(fieldsInGroup))]
    );
  };
  return (
    <Wrapper
      className={
        isPerformanceDateRange
          ? groupName === "Date Range"
            ? "performance-date-range date-range-group"
            : "performance-date-range"
          : undefined
      }>
      <StyledSpan
        {...dragHandleProps}
        ref={groupHeaderRef}
        onClick={toggleCollapse}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        tabIndex={0}
        className={`${isGroupHeaderActive ? "keyboard-focused" : ""} ${
          isCustomColumn ? "allow-hover" : ""
        }`}>
        {customFieldsEditToggle && (
          <DragHandleIconWrapper dragEnabled={customFieldsEditToggle}>
            <DragHandleIcon />
          </DragHandleIconWrapper>
        )}

        {isCustomColumn && (
          <ChevronWrapper>
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
          </ChevronWrapper>
        )}
        {(isUserDefinedColumn || isOrgDefinedColumn || isProjectDefinedColumn) &&
          customFieldsEditToggle && (
            <CheckboxWrapper>
              <CheckboxItem
                checked={
                  fields
                    .filter((obj) => checkedFields.includes(obj.id))
                    .map((obj) => obj.id).length > 0
                }
                onClick={(evt) => evt.stopPropagation()}
                onChange={onCheckboxChange}
              />
            </CheckboxWrapper>
          )}
        {titleRow}
        {isCustomColumn &&
          groupType === GroupType.FieldGroup &&
          !isDisablingManagement && (
            <EditButtonWrapper visible={isHovered}>
              <Tooltip title="Edit Field Group Data">
                <EditDataButton
                  shape="circle"
                  onClick={handleEditDataClick}
                  icon={<Icon path={mdiPencil} size={1} />}
                />
              </Tooltip>
              <Tooltip title="Download Field Group Data">
                <EditDataButton
                  shape="circle"
                  onClick={handleDownload}
                  loading={isDownloading}
                  icon={<Icon path={mdiDownload} size={1} />}
                />
              </Tooltip>
            </EditButtonWrapper>
          )}
      </StyledSpan>

      {isPerformanceDateRange && <Divider style={{ margin: "4px 0" }} />}

      {enabled ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="list">
            {(provided) => (
              <>
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {fieldsList.map((item, index) => {
                    // Calculate if this field item should be active
                    // Fields start at fieldStartIndex + 1 (after the group header)
                    const fieldIndex = fieldStartIndex + 1 + index;
                    const isFieldActive = active && focusedIndex === fieldIndex;
                    // Keep the currently selected field mounted even while the
                    // group is collapsed, so Ctrl+Arrow navigation from it
                    // (or a manual collapse of its own group) never strands it
                    // without a listener. No `!isCustomColumn` check here (unlike
                    // the non-drag branch below) because this branch only renders
                    // when `enabled` is true, which already implies isCustomColumn.
                    const isItemVisible = !isCollapsed || item.id === selectedField?.id;

                    return (
                      <Draggable key={item.id} draggableId={item.id} index={index}>
                        {(provided) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}>
                            {isItemVisible && (
                              <FieldListItem
                                value={item}
                                dragEnabled={fieldsList.length > 1}
                                groups={groups}
                                shortcutsEnabled={shortcutsEnabled}
                                active={isFieldActive}
                                itemRef={
                                  onItemRef && isItemVisible
                                    ? (el) => onItemRef(fieldIndex, el)
                                    : undefined
                                }
                                onFieldClick={onFieldClick}
                                onFieldDoubleClick={onFieldDoubleClick}
                                isDisablingManagement={isDisablingManagement}
                                isCustomColumn={isCustomColumn}
                                onChange={storeProps?.onChange}
                                checkedFields={checkedFields}
                                customFieldsEditToggle={customFieldsEditToggle}
                                selectedField={selectedField}
                                isForecastToggleOn={isForecastToggleOn}
                                selectedForecastFolder={selectedForecastFolder}
                                selectedForecastFolderName={selectedForecastFolderName}
                                selectedPdenSource={selectedPdenSource}
                                isFiltering={isFiltering}
                                categoryColors={categoryColors}
                                entityKind={entityKind}
                                onCheckedFieldsChange={setCheckedFields}
                                onHoveredFieldChange={setHoveredField}
                              />
                            )}
                          </div>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </div>
              </>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <>
          {fieldsList.map((item, index) => {
            // Calculate if this field item should be active
            // Fields start at fieldStartIndex + 1 (after the group header)
            const fieldIndex = fieldStartIndex + 1 + index;
            const isFieldActive = active && focusedIndex === fieldIndex;
            // Keep the currently selected field mounted even while the
            // group is collapsed, so Ctrl+Arrow navigation from it (or a
            // manual collapse of its own group) never strands it without
            // a listener.
            const isItemVisible =
              !isCollapsed || !isCustomColumn || item.id === selectedField?.id;

            if (!isItemVisible) return null;

            return (
              <div key={item.id}>
                <FieldListItem
                  value={item}
                  dragEnabled={false}
                  groups={groups}
                  shortcutsEnabled={shortcutsEnabled}
                  active={isFieldActive}
                  itemRef={
                    onItemRef && isItemVisible
                      ? (el) => onItemRef(fieldIndex, el)
                      : undefined
                  }
                  onFieldClick={onFieldClick}
                  onFieldDoubleClick={onFieldDoubleClick}
                  isDisablingManagement={isDisablingManagement}
                  isCustomColumn={isCustomColumn}
                  onChange={storeProps?.onChange}
                  checkedFields={checkedFields}
                  customFieldsEditToggle={customFieldsEditToggle}
                  selectedField={selectedField}
                  isForecastToggleOn={isForecastToggleOn}
                  selectedForecastFolder={selectedForecastFolder}
                  selectedForecastFolderName={selectedForecastFolderName}
                  selectedPdenSource={selectedPdenSource}
                  isFiltering={isFiltering}
                  categoryColors={categoryColors}
                  entityKind={entityKind}
                  onCheckedFieldsChange={setCheckedFields}
                  onHoveredFieldChange={setHoveredField}
                />
              </div>
            );
          })}
        </>
      )}

      <FieldGroupEditor
        key={groupName}
        visible={editorVisible}
        fieldGroup={groupName}
        showDiscardModal={isDiscardModalOpen}
        onShowDiscardModal={(show) => setIsDiscardModalOpen(show)}
        onClose={() => {
          setEditorVisible(false);
          setIsDiscardModalOpen(false);
          setFieldGroupEditorState({ visible: false, fieldGroup: null });
        }}
        onSave={() => {
          setEditorVisible(false);
          setIsDiscardModalOpen(false);
          setFieldGroupEditorState({ visible: false, fieldGroup: null });
        }}
      />
    </Wrapper>
  );
};

const FieldGroupTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1 1 0%;
  font-size: 1.4rem;
  font-weight: var(--fontWeightBold);
`;

const GroupName = styled.span`
  flex: 1 1 0%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-right: var(--space-4);
`;

const StyledSpan = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-1);
  border-radius: var(--border-radius);
  padding: 2px 8px 2px 0;
  cursor: default;
  outline: none;

  &.allow-hover {
    cursor: pointer;

    &:hover {
      background-color: var(--color-accent-hover);
    }

    &.keyboard-focused {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }

    &:focus {
      outline: 2px solid var(--color-primary);
      outline-offset: -2px;
    }
  }
`;

const Wrapper = styled.div`
  max-width: var(--field-col-max-width);
  cursor: pointer;
  margin: 3px 8px;

  &.performance-date-range {
    margin: 8px;
    padding: 4px 8px;
    border: 1px solid var(--color-surface-04);
    border-radius: var(--border-radius);
  }

  &.date-range-group {
    .field-list-item {
      color: var(--color-primary-hover);
    }
  }
`;

const CheckboxWrapper = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const CheckboxItem = styled(Checkbox)`
  overflow-x: visible !important;
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--color-primary);
    border-color: var(--color-primary);
  }
`;

const DragHandleIconWrapper = styled.span`
  visibility: ${({ dragEnabled }) => (dragEnabled ? "visible" : "hidden")};
  color: #c9d1d2;
  font-size: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ChevronWrapper = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const TestIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  color: #7fbf3f;
  flex-shrink: 0;
`;

const EditButtonWrapper = styled.div<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  transition: opacity 0.2s;
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EditDataButton = styled(Button)`
  color: #a2aaad;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  min-width: 22px;
  width: 22px;
  height: 22px;
  border: none;
  box-shadow: none;

  &:hover {
    color: var(--color-primary);
  }

  &:focus {
    background: transparent;
    border: none;
  }

  .anticon {
    font-size: 16px;
  }
`;

export default FieldGroup;
